'use client'

import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import AppStoreLink from './AppStoreLink'
import { track } from '@/utils/analytics'

/**
 * ScanDemoVideo — the "show, don't tell" moment the site has been missing.
 *
 * Why this exists: Supabase shows 91% of installs never scan once. The
 * #1 reason a visitor never scans after install is that they didn't know
 * what the scan experience LOOKED like before they downloaded. Cal AI and
 * Yuka both lead their landing pages with a real scan video. Ours has
 * never had one.
 *
 * The asset is a real 40-second iPhone screen recording of an iced-drink
 * (plastic cold cup) scan, full length, uncropped. It's autoplay-muted-loop
 * because:
 *  - muted + playsInline is the only way iOS autoplays
 *  - loop is the only way it works without a tap on desktop too
 *  - we strip audio in encoding so there's literally no audio to leak
 *
 * Two visual variants:
 *  - `hero` (default): side-by-side panel for the homepage. Video on the
 *    right, three timestamped moments + dual CTA on the left. Asymmetric
 *    on purpose — the rest of the homepage is centered, so this section
 *    is the one that breaks the rhythm. Per taste-skill: VARIANCE > uniformity.
 *  - `inline`: compact full-width version for in-article placement on
 *    blog posts. No side annotations; visual moment only with a single CTA
 *    underneath. Used inside `FirstScanCallout`.
 *
 * Tracking:
 *  - `scan_demo_impression` — fires once when the section enters viewport
 *  - `scan_demo_play` — fires once the video has actually played (not just
 *    rendered). Useful because iOS Low Power Mode silently blocks autoplay
 *    even with muted+playsinline.
 *  - `scan_demo_appstore_click` — primary CTA
 *  - `scan_demo_waitlist_click` — secondary CTA
 *
 * Performance:
 *  - `preload="metadata"` only — never download the full file until the
 *    section is visible. The IntersectionObserver in this component
 *    triggers a real `preload="auto"` swap when the user is within 200px.
 *  - poster is a single 56KB JPG so the first paint is instant even on 3G.
 *  - All three sources (mp4, webm, poster) sit in /public/scan-demo/.
 */
export interface ScanDemoVideoProps {
  /** Visual variant. `hero` = side-by-side with annotations + dual CTA.
   *  `inline` = compact, video-only, single CTA underneath. */
  variant?: 'hero' | 'inline'
  /** Stable identifier for GA4 attribution. Defaults differ per variant. */
  surface?: string
  /** Override the eyebrow text. Useful for in-article placements. */
  eyebrow?: string
  /** Override the H2/headline. Default is variant-specific. */
  headline?: string
  /** Override the sub-headline. Default is variant-specific. */
  sub?: string
}

/** Three on-video moments. Timestamps match the 40-second clip exactly;
 *  if the source video changes, re-sync these. */
const MOMENTS: Array<{ t: string; label: string; detail: string }> = [
  {
    t: '0:08',
    label: 'Point at any cup, can, or package',
    detail: 'No barcode required. The AI reads the item end to end.',
  },
  {
    t: '0:20',
    label: 'Exposure score and main driver lock in',
    detail: 'It pinpoints the cup and lid as the plastic contact, with the research behind it.',
  },
  {
    t: '0:31',
    label: 'Safer swap, saved to your routine',
    detail: 'A stainless or glass swap worth 22 points, one tap to save and track.',
  },
]

export default function ScanDemoVideo({
  variant = 'hero',
  surface,
  eyebrow,
  headline,
  sub,
}: ScanDemoVideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const [hasPlayed, setHasPlayed] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const trackedImpression = useRef(false)
  const trackedPlay = useRef(false)

  // Stable surface label per variant so GA4 attribution is clean.
  const resolvedSurface =
    surface ?? (variant === 'hero' ? 'home_scan_demo' : 'inline_scan_demo')

  // Variant-specific copy. Hero gets the full pitch; inline gets a short,
  // article-context-friendly headline that pairs with FirstScanCallout copy.
  const resolvedEyebrow = eyebrow ?? (variant === 'hero' ? 'Real scan · 40 seconds' : 'See it before you install')
  const resolvedHeadline =
    headline ??
    (variant === 'hero'
      ? 'This is exactly what a scan looks like.'
      : 'Watch a real scan run end to end.')
  const resolvedSub =
    sub ??
    (variant === 'hero'
      ? 'No edits, no demo screen. A real iced-drink scan from open camera to a saved safer swap in forty seconds.'
      : 'Same camera flow you\'ll use in the app: bottle in, score out.')

  // ─────────────────────────────────────────────────────────────────────
  // IntersectionObserver — single one drives both:
  //   (a) the impression event (fires once at 40% visible)
  //   (b) the preload="auto" swap so the file only downloads when needed
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = sectionRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            setIsVisible(true)
            if (!trackedImpression.current) {
              trackedImpression.current = true
              track('scan_demo_impression', {
                cta_surface: resolvedSurface,
                page_type: variant === 'hero' ? 'home' : 'blog',
              })
            }
          }
        }
      },
      { threshold: [0.4], rootMargin: '200px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [resolvedSurface, variant])

  // Once we've signalled visibility, ask the video element to start. Some
  // mobile browsers refuse autoplay even with muted+playsinline (Low Power
  // Mode on iOS is the common one) — calling .play() explicitly recovers
  // those cases without breaking the ones that auto-started.
  useEffect(() => {
    if (!isVisible) return
    const v = videoRef.current
    if (!v) return
    const playPromise = v.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      // Swallow — autoplay rejection is expected on some configs, and the
      // poster still shows the user enough context to click through.
      playPromise.catch(() => {})
    }
  }, [isVisible])

  // First real `playing` event = the video is actually displaying frames.
  // This distinguishes a successful autoplay from a poster-only fallback.
  const handlePlaying = () => {
    if (hasPlayed) return
    setHasPlayed(true)
    if (!trackedPlay.current) {
      trackedPlay.current = true
      track('scan_demo_impression', {
        cta_surface: `${resolvedSurface}_played`,
        page_type: variant === 'hero' ? 'home' : 'blog',
      })
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Inline (compact) variant — used in-article. No annotations panel; the
  // surrounding FirstScanCallout supplies the article-specific copy. We
  // keep the same video element so the asset is cached across surfaces.
  // ─────────────────────────────────────────────────────────────────────
  if (variant === 'inline') {
    return (
      // overflow-hidden on the outer wrapper prevents the inset blur from
      // pushing the in-article layout wider than its column on narrow
      // viewports (mirror of the bug fix on the hero variant).
      <div ref={sectionRef} className="not-prose my-6 overflow-hidden">
        <div className="relative w-fit mx-auto">
          {/* Soft brand halo behind the phone screen — subtle warmth so the
              video doesn't sit on a flat background. Pure decoration. */}
          <div
            aria-hidden="true"
            className="absolute -inset-4 sm:-inset-6 rounded-[40px] bg-gradient-to-br from-warmOrange/20 via-warmOrange/5 to-transparent blur-2xl pointer-events-none"
          />
          <video
            ref={videoRef}
            width={540}
            height={1098}
            className="relative block w-[min(82vw,260px)] h-auto aspect-[540/1098] rounded-[28px] shadow-2xl shadow-warmOrange/15 ring-1 ring-black/5 bg-black object-cover"
            playsInline
            muted
            loop
            preload={isVisible ? 'auto' : 'metadata'}
            poster="/scan-demo/scan-demo-poster.jpg"
            aria-label="Real iPhone screen recording of a 40-second microplastic scan"
            onPlaying={handlePlaying}
          >
            <source src="/scan-demo/scan-demo.webm" type="video/webm" />
            <source src="/scan-demo/scan-demo.mp4" type="video/mp4" />
          </video>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────
  // Hero variant — homepage above-the-fold panel. Asymmetric grid:
  //   - Left col (text): 5/12 on desktop, full on mobile
  //   - Right col (video): 7/12 on desktop, full on mobile, max-h:600 to
  //     keep it from dominating the viewport on tall screens.
  // ─────────────────────────────────────────────────────────────────────
  return (
    <section
      ref={sectionRef}
      // overflow-hidden is load-bearing — the decorative halo and "Live
      // demo" badge intentionally overflow the video element itself, so
      // without a clipping parent they can push the page wider than the
      // mobile viewport (causes the "video looks cut off on the right"
      // bug reported on iPhone 17 Pro / 402pt viewport).
      className="relative w-full overflow-hidden bg-gradient-to-b from-cream to-white py-14 sm:py-section"
      aria-labelledby="scan-demo-heading"
    >
      {/* The video is a fixed-width w-fit phone frame, so any column wider than
          the frame itself just turns into dead space between it and the copy.
          Keep the video column tight (5) and give the surplus to the text (7),
          which is the block that actually benefits from the width. */}
      <div className="frame grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-10 items-center">
        {/* ── Right: copy + moments + CTA (video leads on desktop) ───── */}
        <motion.div
          className="md:col-span-7 order-2"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-warmOrange mb-3">
            {resolvedEyebrow}
          </p>
          <h2
            id="scan-demo-heading"
            className="text-[clamp(1.75rem,4.5vw,2.5rem)] font-bold text-charcoal leading-[1.1] mb-4"
          >
            {resolvedHeadline}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-7 leading-relaxed">
            {resolvedSub}
          </p>

          <ol className="space-y-4 mb-8">
            {MOMENTS.map((m, i) => (
              <motion.li
                key={m.t}
                className="flex gap-4"
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
              >
                <span
                  aria-hidden="true"
                  className="shrink-0 inline-flex items-center justify-center min-w-[52px] h-7 px-2 rounded-full bg-charcoal text-white text-2xs font-mono font-semibold tracking-wider"
                >
                  {m.t}
                </span>
                <span className="leading-snug">
                  <span className="block font-semibold text-charcoal text-base sm:text-base">
                    {m.label}
                  </span>
                  <span className="block text-sm text-gray-500 mt-0.5">{m.detail}</span>
                </span>
              </motion.li>
            ))}
          </ol>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <AppStoreLink
              source={`${resolvedSurface}_appstore`}
              ctaPosition="scan_demo"
              trackingParams={{ page_type: 'home' }}
              className="inline-flex items-center justify-center bg-charcoal text-white px-6 py-3.5 rounded-control hover:bg-charcoal/90 transition-[color,background-color,border-color,text-decoration-color] duration-200 shadow-lg shadow-charcoal/20 min-h-[48px] text-base font-semibold pressable"
            >
              <svg className="w-5 h-5 mr-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              Get 3 free scans on iOS
            </AppStoreLink>
            <a
              href="#android-waitlist"
              onClick={() =>
                track('android_waitlist_open', {
                  cta_surface: `${resolvedSurface}_waitlist`,
                  page_type: 'home',
                })
              }
              className="inline-flex items-center justify-center text-charcoal/80 hover:text-charcoal underline underline-offset-4 decoration-charcoal/30 hover:decoration-charcoal text-sm font-medium px-2 py-2 min-h-[48px]"
            >
              On Android? Join the waitlist →
            </a>
          </div>
        </motion.div>

        {/* ── Left: the actual phone video ─────────────────────────────
            Sizing strategy:
              - Width capped at 88% of viewport on mobile (so the iPhone
                17 Pro at 402pt gives ~354px of usable width and the
                video lands at ~260px with comfortable margins) and a
                fixed pixel value from sm: up so desktop layout is stable.
              - Explicit aspect-[540/1098] locks the source video's
                aspect ratio so the element renders without object-fit
                stretch and without max-h reflow.
              - The decorative halo + "Live demo" badge sit inside a
                relative wrapper sized `w-fit` so the centering math
                follows the video, not the (wider) halo. Combined with
                the section's overflow-hidden, this kills the "cut off
                on the right" bug. */}
        <motion.div
          className="md:col-span-5 order-1 flex justify-center md:justify-start"
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className="relative w-fit mx-auto md:mx-0">
            {/* Soft warmth behind the device. Pure decoration; aria-hidden.
                Tightened from -inset-10 → -inset-6 on mobile so the halo
                never pushes outside the visible area. */}
            <div
              aria-hidden="true"
              className="absolute -inset-6 sm:-inset-10 md:-inset-14 rounded-[60px] bg-gradient-to-br from-warmOrange/25 via-warmOrange/8 to-transparent blur-3xl pointer-events-none"
            />
            {/* Floating badge — sits flush with the video corner on
                mobile (top-2/left-2 inside the frame) and floats out
                slightly on sm+ for the taste-skill variance move.
                Negative offsets on mobile were causing horizontal
                bleed at small viewports. */}
            <motion.div
              className="absolute top-2 left-2 sm:-top-3 sm:-left-4 z-10 inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full pl-2 pr-3 py-1 shadow-lg shadow-warmOrange/25 ring-1 ring-black/5"
              initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: 0.3, type: 'spring', stiffness: 140 }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-warmOrange opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-warmOrange" />
              </span>
              <span className="text-[10px] sm:text-2xs font-semibold uppercase tracking-wider text-charcoal">
                Live demo
              </span>
            </motion.div>

            <video
              ref={videoRef}
              width={540}
              height={1098}
              className="relative block w-[min(88vw,300px)] sm:w-[300px] md:w-[320px] h-auto aspect-[540/1098] rounded-[36px] sm:rounded-[40px] shadow-2xl shadow-warmOrange/20 ring-1 ring-black/10 bg-black object-cover"
              playsInline
              muted
              loop
              preload={isVisible ? 'auto' : 'metadata'}
              poster="/scan-demo/scan-demo-poster.jpg"
              aria-label="Real iPhone screen recording of a 40-second microplastic scan"
              onPlaying={handlePlaying}
            >
              <source src="/scan-demo/scan-demo.webm" type="video/webm" />
              <source src="/scan-demo/scan-demo.mp4" type="video/mp4" />
              {/* SR-only fallback. Browsers that can't play either format
                  will show this text inside the <video> element. */}
              <p className="p-4 text-sm text-white">
                Your browser can&apos;t play this video. The MicroPlastics app
                runs on iOS. Get 3 free scans on the App Store.
              </p>
            </video>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
